import React,{useCallback,useEffect,useState}from"react";
import{Box,Button,Checkbox,Dialog,DialogActions,DialogContent,DialogTitle,FormControlLabel,IconButton,Typography,TextField}from"@mui/material";
import AddRounded from"@mui/icons-material/AddRounded";
import CloseRounded from"@mui/icons-material/CloseRounded";
import EditOutlined from"@mui/icons-material/EditOutlined";
import LockResetOutlined from"@mui/icons-material/LockResetOutlined";
import{useAuth}from"../../context/AuthContext";
import{useAlert}from"../../context/AlertContext";

const initialForm={name:"",email:"",phone:"",active:true};

export default function AdminTechnicians(){
  const{authFetch}=useAuth();
  const{showSuccess,showError,showWarning}=useAlert();
  const[items,setItems]=useState([]);
  const[loading,setLoading]=useState(true);
  const[saving,setSaving]=useState(false);
  const[actionId,setActionId]=useState(null);
  const[open,setOpen]=useState(false);
  const[editing,setEditing]=useState(null);
  const[form,setForm]=useState(initialForm);

  const loadData=useCallback(async()=>{
    setLoading(true);

    try{
      const response=await authFetch("/api/login/technicians/");
      const data=await response.json();

      if(!response.ok)throw new Error(data.detail||"No se pudieron cargar los técnicos.");

      setItems(Array.isArray(data)?data:[]);
    }catch(error){
      showError(error.message||"No se pudieron cargar los técnicos.");
    }finally{
      setLoading(false);
    }
  },[authFetch,showError]);

  useEffect(()=>{
    loadData();
  },[loadData]);

  const handleChange=(e)=>{
    const{name,value,checked,type}=e.target;
    setForm(prev=>({...prev,[name]:type==="checkbox"?checked:value}));
  };

  const openCreate=()=>{
    setEditing(null);
    setForm(initialForm);
    setOpen(true);
  };

  const openEdit=(item)=>{
    setEditing(item);
    setForm({
      name:item.name||"",
      email:item.email||"",
      phone:item.phone||"",
      active:Boolean(item.active),
    });
    setOpen(true);
  };

  const closeDialog=()=>{
    if(saving)return;
    setOpen(false);
    setEditing(null);
    setForm(initialForm);
  };

  const getError=(data,fallback)=>{
    if(data?.detail)return data.detail;
    if(data?.name?.[0])return data.name[0];
    if(data?.email?.[0])return data.email[0];
    if(data?.phone?.[0])return data.phone[0];
    if(data?.active?.[0])return data.active[0];
    return fallback;
  };

  const submit=async()=>{
    if(!form.name.trim()||!form.email.trim()){
      showWarning("Nombre y correo son obligatorios.");
      return;
    }

    setSaving(true);

    try{
      const response=await authFetch(
        editing
          ?`/api/login/technicians/${editing.public_id}/`
          :"/api/login/technicians/",
        {
          method:editing?"PATCH":"POST",
          headers:{"Content-Type":"application/json"},
          body:JSON.stringify({
            name:form.name.trim(),
            email:form.email.trim().toLowerCase(),
            phone:form.phone.trim(),
            active:form.active,
          }),
        }
      );

      const data=await response.json();

      if(!response.ok){
        throw new Error(
          getError(
            data,
            editing
              ?"No se pudo actualizar el técnico."
              :"No se pudo crear el técnico."
          )
        );
      }

      showSuccess(
        editing
          ?"Técnico actualizado correctamente."
          :"Técnico creado e invitación enviada correctamente."
      );

      setOpen(false);
      setEditing(null);
      setForm(initialForm);
      await loadData();

    }catch(error){
      showError(error.message||"No se pudo guardar el técnico.");
    }finally{
      setSaving(false);
    }
  };

  const toggleStatus=async(item)=>{
    setActionId(`status-${item.public_id}`);

    try{
      const response=await authFetch(
        `/api/login/technicians/${item.public_id}/`,
        {
          method:"PATCH",
          headers:{"Content-Type":"application/json"},
          body:JSON.stringify({
            active:!item.active,
          }),
        }
      );

      const data=await response.json();

      if(!response.ok){
        throw new Error(
          getError(
            data,
            "No se pudo cambiar el estado del técnico."
          )
        );
      }

      showSuccess(
        item.active
          ?"Técnico inactivado correctamente."
          :"Técnico activado correctamente."
      );

      await loadData();

    }catch(error){
      showError(
        error.message||
        "No se pudo cambiar el estado del técnico."
      );
    }finally{
      setActionId(null);
    }
  };

  const resetPassword=async(item)=>{
    setActionId(`reset-${item.public_id}`);

    try{
      const response=await authFetch(
        `/api/login/technicians/${item.public_id}/reset-password/`,
        {
          method:"POST",
        }
      );

      const data=await response.json();

      if(!response.ok){
        throw new Error(
          data.detail||
          "No se pudo restablecer la contraseña."
        );
      }

      showSuccess(
        data.detail||
        "Nueva contraseña enviada correctamente."
      );

    }catch(error){
      showError(
        error.message||
        "No se pudo restablecer la contraseña."
      );
    }finally{
      setActionId(null);
    }
  };

  const fieldSx={
    "& .MuiOutlinedInput-root":{
      borderRadius:2
    }
  };

  return(
    <Box>
      <Box sx={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:2,mb:2.5}}>
        <Typography sx={{fontSize:24,fontWeight:700,color:"#182230"}}>
          Técnicos
        </Typography>

        <Button
          variant="contained"
          startIcon={<AddRounded/>}
          onClick={openCreate}
          sx={{
            bgcolor:"#123f6d",
            borderRadius:2,
            textTransform:"none",
            fontWeight:700,
            boxShadow:"none",
            "&:hover":{
              bgcolor:"#0e345b",
              boxShadow:"none"
            }
          }}
        >
          Crear técnico
        </Button>
      </Box>

      <Box sx={{bgcolor:"#fff",border:"1px solid #dce3ea",borderRadius:3,overflowX:"auto"}}>
        <Box sx={{minWidth:850}}>
          <Box sx={{display:"grid",gridTemplateColumns:"1.4fr 1.8fr 1.2fr .8fr 1.5fr",gap:2,px:2.5,py:1.5,bgcolor:"#123f6d",color:"#fff"}}>
            {["Nombre","Correo","Teléfono","Estado","Acciones"].map(label=>(
              <Typography key={label} sx={{fontSize:13,fontWeight:700}}>
                {label}
              </Typography>
            ))}
          </Box>

          {loading&&(
            <Box sx={{py:6,textAlign:"center"}}>
              <Typography sx={{fontSize:14,color:"#667085"}}>
                Cargando técnicos...
              </Typography>
            </Box>
          )}

          {!loading&&items.length===0&&(
            <Box sx={{py:6,textAlign:"center"}}>
              <Typography sx={{fontSize:14,color:"#667085"}}>
                No hay técnicos registrados.
              </Typography>
            </Box>
          )}

          {!loading&&items.map(item=>(
            <Box
              key={item.public_id}
              sx={{
                display:"grid",
                gridTemplateColumns:"1.4fr 1.8fr 1.2fr .8fr 1.5fr",
                gap:2,
                px:2.5,
                py:1.5,
                alignItems:"center",
                borderBottom:"1px solid #edf1f5",
                "&:last-child":{
                  borderBottom:"none"
                }
              }}
            >
              <Typography sx={{fontSize:13.5,fontWeight:700,color:"#182230"}}>
                {item.name}
              </Typography>

              <Typography sx={{fontSize:13.5,color:"#344054",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                {item.email}
              </Typography>

              <Typography sx={{fontSize:13.5,color:"#344054"}}>
                {item.phone||"-"}
              </Typography>

              <Box sx={{width:"fit-content",px:1.1,py:.4,borderRadius:5,bgcolor:item.active?"#e9f7ef":"#fdecec",color:item.active?"#207a45":"#b42318",fontSize:12,fontWeight:700}}>
                {item.active?"Activo":"Inactivo"}
              </Box>

              <Box sx={{display:"flex",gap:.5,alignItems:"center"}}>
                <IconButton
                  size="small"
                  onClick={()=>openEdit(item)}
                  disabled={actionId!==null}
                  title="Editar"
                >
                  <EditOutlined sx={{fontSize:18}}/>
                </IconButton>

                <IconButton
                  size="small"
                  onClick={()=>resetPassword(item)}
                  disabled={actionId!==null}
                  title="Restablecer contraseña"
                >
                  <LockResetOutlined sx={{fontSize:18}}/>
                </IconButton>

                <Button
                  size="small"
                  variant="outlined"
                  disabled={actionId!==null}
                  onClick={()=>toggleStatus(item)}
                  sx={{
                    borderRadius:2,
                    textTransform:"none",
                    fontWeight:700,
                    fontSize:11.5,
                    color:item.active?"#b42318":"#207a45",
                    borderColor:item.active?"#f0b7b3":"#a9d8bc",
                    "&:hover":{
                      borderColor:item.active?"#b42318":"#207a45",
                      bgcolor:item.active?"#fff6f5":"#f0faf4"
                    }
                  }}
                >
                  {actionId===`status-${item.public_id}`
                    ?"Procesando..."
                    :item.active
                      ?"Inactivar"
                      :"Activar"
                  }
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Dialog
        open={open}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        disableScrollLock
        PaperProps={{
          sx:{
            borderRadius:3,
            boxShadow:"none",
            border:"1px solid #dce3ea"
          }
        }}
      >
        <DialogTitle sx={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:20,fontWeight:700,color:"#182230"}}>
          {editing?"Editar técnico":"Crear técnico"}

          <IconButton
            onClick={closeDialog}
            disabled={saving}
          >
            <CloseRounded/>
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",md:"1fr 1fr"},gap:2,pt:1}}>
            <TextField
              label="Nombre"
              name="name"
              value={form.name}
              onChange={handleChange}
              size="small"
              fullWidth
              sx={fieldSx}
            />

            <TextField
              label="Teléfono"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              size="small"
              fullWidth
              sx={fieldSx}
            />

            <TextField
              label="Correo"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              size="small"
              fullWidth
              sx={{
                ...fieldSx,
                gridColumn:{
                  xs:"auto",
                  md:"1 / -1"
                }
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="active"
                  checked={form.active}
                  onChange={handleChange}
                  size="small"
                />
              }
              label={
                <Typography sx={{fontSize:13.5}}>
                  Cuenta activa
                </Typography>
              }
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{px:3,pb:2.5}}>
          <Button
            onClick={closeDialog}
            disabled={saving}
            sx={{
              textTransform:"none",
              color:"#667085",
              fontWeight:700
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={submit}
            disabled={saving}
            sx={{
              bgcolor:"#123f6d",
              borderRadius:2,
              textTransform:"none",
              fontWeight:700,
              boxShadow:"none",
              "&:hover":{
                bgcolor:"#0e345b",
                boxShadow:"none"
              }
            }}
          >
            {saving
              ?"Guardando..."
              :editing
                ?"Guardar cambios"
                :"Crear técnico"
            }
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}